'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef, useState } from 'react';
import { useOverlay } from '@/app/components/overlays/hooks/useOverlay';
import { Modal } from '@/app/components/overlays/modal';
import { SPLASH_ROOT_ATTRIBUTE } from '@/app/components/splash/constants';
import { SPLASH_DATA_GA, SPLASH_DATA_TEST } from '@/app/components/splash/tracking';
import { SplashUI } from '@/app/components/splash/ui';
import { writeSplashAccepted } from '@/app/components/splash/utils';
import { OVERLAY_KEYS } from '@/app/constants/overlayKeys';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { acceptSplash } from '@/services/splash/accept-splash';
import type { SplashPopup } from '@/services/splash/types';
import { trackDynamicEvents } from '@/tag-manager';
const SPLASH_INITIAL_VISIBLE_CLASS = 'opacity-100 duration-0';
export interface SplashModalProps {
  splash: SplashPopup | null;
  loadImageImmediately: boolean;
  onClose: () => void;
}
export const SplashModal = t0 => {
  const $ = _c(28);
  const {
    splash,
    loadImageImmediately,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      overlayKey: OVERLAY_KEYS.SPLASH,
      instanceId: "splash"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const splashOverlay = useOverlay(t1);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const dontShowAgainRef = useRef(false);
  const closedRef = useRef(false);
  const splashId = splash?.id ?? null;
  let t2;
  if ($[1] !== splash) {
    t2 = () => {
      if (!splash) {
        return;
      }
      writeSplashAccepted(splash);
      acceptSplash(splash).catch(_temp);
    };
    $[1] = splash;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const persistDontShowAgainAccept = t2;
  if (!splash || !splashId) {
    return null;
  }
  let t3;
  if ($[3] !== onClose || $[4] !== persistDontShowAgainAccept || $[5] !== splashOverlay) {
    t3 = area => {
      if (closedRef.current) {
        return;
      }
      closedRef.current = true;
      if (dontShowAgainRef.current) {
        persistDontShowAgainAccept();
      }
      trackDynamicEvents({
        event_name: SPLASH_DATA_GA.CLOSE,
        group_name: "splash",
        area,
        value: dontShowAgainRef.current ? SPLASH_DATA_GA.DONT_SHOW_AGAIN_CHECKED : SPLASH_DATA_GA.DONT_SHOW_AGAIN_UNCHECKED
      });
      splashOverlay.close();
      onClose();
    };
    $[3] = onClose;
    $[4] = persistDontShowAgainAccept;
    $[5] = splashOverlay;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleClose = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = next => {
      dontShowAgainRef.current = next;
      setDontShowAgain(next);
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleDontShowAgainChange = t4;
  let t5;
  if ($[8] !== handleClose) {
    t5 = area_0 => {
      trackDynamicEvents({
        event_name: SPLASH_DATA_GA.ENTER_CLICK,
        group_name: "splash",
        area: area_0,
        value: dontShowAgainRef.current ? SPLASH_DATA_GA.DONT_SHOW_AGAIN_CHECKED : SPLASH_DATA_GA.DONT_SHOW_AGAIN_UNCHECKED
      });
      handleClose(area_0);
    };
    $[8] = handleClose;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleEnter = t5;
  let t6;
  if ($[10] !== handleClose) {
    t6 = area_1 => {
      trackDynamicEvents({
        event_name: SPLASH_DATA_GA.CTA_CLICK,
        group_name: "splash",
        area: area_1,
        value: dontShowAgainRef.current ? SPLASH_DATA_GA.DONT_SHOW_AGAIN_CHECKED : SPLASH_DATA_GA.DONT_SHOW_AGAIN_UNCHECKED
      });
      handleClose(area_1);
    };
    $[10] = handleClose;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleNavigate = t6;
  const t7 = splashOverlay.overlayProps;
  let t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = createDataAttributes({
      test: SPLASH_DATA_TEST.splash
    });
    t9 = cn("fixed", SPLASH_INITIAL_VISIBLE_CLASS);
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  let t10;
  if ($[14] !== handleClose) {
    t10 = () => handleClose("outside");
    $[14] = handleClose;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      [SPLASH_ROOT_ATTRIBUTE]: true
    };
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== dontShowAgain || $[18] !== handleEnter || $[19] !== handleNavigate || $[20] !== loadImageImmediately || $[21] !== splash) {
    t12 = <div className="overflow-hidden rounded-sm lg:rounded-md"><SplashUI splash={splash} dontShowAgain={dontShowAgain} loadImageImmediately={loadImageImmediately} onDontShowAgainChange={handleDontShowAgainChange} onEnter={handleEnter} onNavigate={handleNavigate} /></div>;
    $[17] = dontShowAgain;
    $[18] = handleEnter;
    $[19] = handleNavigate;
    $[20] = loadImageImmediately;
    $[21] = splash;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== splashOverlay.overlayProps || $[24] !== t10 || $[25] !== t12 || $[26] !== t8) {
    t13 = <Modal {...t7} autoFocus={false} autoHeight={false} backdropBlur="xl" enforceFocus={false} isOpen={true} overlayClassName="z-70" animateBaseClassName="opacity-100 duration-0" animateEnterClassName="opacity-100" animateExitClassName="opacity-0" animateTransitionClassName="transition-opacity duration-300 ease-in-out" className="bg-transparent px-8 md:px-10 lg:px-0" contentProps={t8} backdropClassName={t9} isScrollOutside={true} scrollOutsideWrapperClassName="relative h-auto w-full max-w-140 lg:max-w-212 my-10" onClose={t10} {...t11}>{t12}</Modal>;
    $[23] = splashOverlay.overlayProps;
    $[24] = t10;
    $[25] = t12;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
};
function _temp() {}